import {
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, {
  memo,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import PlusIcon from 'common/shared-ui/src/icons/PlusIcon';

import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import SpreadsheetMobileIssueButton from '../SpreadsheetMobileIssueButton/SpreadsheetMobileIssueButton';
import SpreadsheetWeek from '../SpreadsheetWeek';
import { formatWeek } from '../formatWeek';

// Original class: SpreadsheetTableRoot
const StyledSpreadsheetTableRoot = styled(Table)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(4),

  '& .MuiTableCell-root': {
    borderBottom: 0,
  },
}));
// Original class: tableCell
const StyledTableCell = styled(TableCell)(theme => ({
  boxShadow: '1px 1px #E4E4EF',
  width: 220,
}));
// Original class: tableCellSmall
const StyledTableCellSmall = styled(TableCell)(({ theme }) => ({
  width: 100,
  boxShadow: '0 1px #E4E4EF',

  '&.MuiTableCell-root.MuiTableCell-paddingNone': {
    padding: '0 !important',
  },

  '&.MuiTableCell-root:not(.MuiTableCell-head)': {
    padding: theme.spacing(4.5, 7),
  },
}));
// Original class: tableCellMedium
const StyledTableCellMedium = styled(TableCell)(theme => ({
  boxShadow: '1px 1px #E4E4EF',
  width: 95,
}));
// Original class: bottomRow
const StyledBottomRow = styled(TableRow)(theme => ({
  '& .MuiTableCell-root': {
    borderBottom: 0,
    boxShadow: '1px 0px #E4E4EF',
  },

  '& .MuiTableCell-root:last-child': {
    boxShadow: 'unset',
  },
}));
// Original class: addRowButton
const StyledAddRowButton = styled(IconButton)(theme => ({
  '&.MuiButtonBase-root': {
    padding: 0,
  },
}));

const SpreadsheetTable = ({
  weekDataHeader,
  startOfWeek,
  endOfWeek,
  showOnlyFilledIn,
  tableFooterDaysSum,
  formattedIssues,
  editedFormattedIssues,
  getAnotherProjectsData,
  anotherProjects,
  formattedAnotherProjects,
  tableSum,
  setTableSum,
  timeType,
  timeTypes,
  query,
  setQuery,
}) => {
  const getLocalString = useTranslation();
  const [workDaysCount, setWorkDaysCount] = useState(0);
  const [workDayHours, setWorkDayHours] = useState(0);
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('lg'));

  useEffect(() => {
    setTableSum(0);
    tableFooterDaysSum.forEach(daySum => {
      setTableSum(
        prevState =>
          prevState +
          daySum[
            timeType === timeTypes.MAIN_HOURS
              ? 'totalNormalHours'
              : 'totalOvertimeHours'
          ],
      );
    });
    setWorkDaysCount(
      tableFooterDaysSum.filter(day => day.expectedHours !== 0).length,
    );
    setWorkDayHours(
      tableFooterDaysSum
        .filter(day => day.totalNormalHours !== 0)
        .reduce(
          (acc, current) =>
            acc +
            parseFloat(
              Math.abs(
                current.totalNormalHours - current.expectedHours,
              ).toFixed(1),
            ),
          0,
        ),
    );
  }, [setTableSum, tableFooterDaysSum, timeType, timeTypes.MAIN_HOURS]);

  const handleHideWeek = useCallback(
    e => {
      timeType === timeTypes.MAIN_HOURS
        ? (formattedIssues[
            e.currentTarget.attributes.issueid.value
          ].hiddenMainHour = true)
        : (formattedIssues[
            e.currentTarget.attributes.issueid.value
          ].hiddenOvertimeHour = true);
      return forceUpdate();
    },
    [formattedIssues, timeType, timeTypes.MAIN_HOURS],
  );

  const getCurrentIssueWeek = useCallback(
    (issueId, type) =>
      formattedIssues[issueId][
        type === timeTypes.MAIN_HOURS ? 'weekDays' : 'overTimeWeekDays'
      ],
    [formattedIssues, timeTypes.MAIN_HOURS],
  );

  const getCurrentWeekDayIndex = useCallback(
    (currentWeek, date) =>
      currentWeek.reduce((acc, day, index) => {
        if (dayjs(day.date).format() === dayjs(date).format()) {
          return index;
        }
        return acc;
      }, 0),
    [],
  );

  const handleChangeDayHour = useCallback(
    (newValue, issueId, date, type) => {
      let regex = /^[0-9]{1,3}([,.][0-9]{0,2})*$/;
      const currentIssue = formattedIssues[issueId];
      const currentWeek = getCurrentIssueWeek(issueId, type);
      const currentWeekDayIndex = getCurrentWeekDayIndex(currentWeek, date);
      currentWeek[currentWeekDayIndex].isEdited = true;
      currentWeek[currentWeekDayIndex].hours = regex.test(newValue)
        ? newValue
        : 0;
      currentWeek[currentWeekDayIndex].issueId = currentIssue.issueId;
      currentWeek[currentWeekDayIndex].issueUniqueId =
        currentIssue.issueUniqueId;
      editedFormattedIssues[currentIssue.issueUniqueId] = currentIssue;
    },
    [
      editedFormattedIssues,
      formattedIssues,
      getCurrentIssueWeek,
      getCurrentWeekDayIndex,
    ],
  );

  const handleChangeDayComment = useCallback(
    (newValue, issueId, date, type) => {
      const currentIssue = formattedIssues[issueId];
      const currentWeek = getCurrentIssueWeek(issueId, type);
      const currentWeekDayIndex = getCurrentWeekDayIndex(currentWeek, date);
      currentWeek[currentWeekDayIndex].isEdited = true;
      currentWeek[currentWeekDayIndex].comments = newValue;
      if (
        currentWeek[currentWeekDayIndex].redmineId === 0 &&
        newValue.length === 0
      ) {
        currentWeek[currentWeekDayIndex].isEdited = false;
      }
      editedFormattedIssues[currentIssue.issueUniqueId] = currentIssue;
    },
    [
      editedFormattedIssues,
      formattedIssues,
      getCurrentIssueWeek,
      getCurrentWeekDayIndex,
    ],
  );

  const counter = useRef(0);

  const handleCreateNewProject = useCallback(async () => {
    if (anotherProjects.length === 0) {
      await getAnotherProjectsData({ start: startOfWeek, end: endOfWeek });
    }
    const issueTemplate = {
      issueId: `_${counter.current}`,
      issueUniqueId: `_${counter.current}`,
      issueName: `_${counter.current}`,
      projectName: `_${counter.current}`,
      weekDays: formatWeek({ startOfWeek }),
      overTimeWeekDays: formatWeek({ startOfWeek }),
      totalNormalHours: 0,
      totalOvertimeHours: 0,
    };
    formattedIssues[issueTemplate.issueUniqueId] = issueTemplate;
    counter.current = counter.current + 1;
    forceUpdate();
  }, [
    anotherProjects.length,
    startOfWeek,
    formattedIssues,
    getAnotherProjectsData,
    endOfWeek,
  ]);

  const handleSelectNewProject = useCallback(
    (projectId, issue) => {
      const currentProject = anotherProjects.find(
        project => Number(project.projectId) === Number(projectId),
      );
      formattedIssues[issue.issueUniqueId].projectName =
        currentProject.projectName;
      formattedIssues[issue.issueUniqueId].projectId = currentProject.projectId;
      formattedIssues[issue.issueUniqueId].weekDays = formatWeek({
        startOfWeek,
        project: currentProject,
      });
    },
    [anotherProjects, formattedIssues, startOfWeek],
  );

  const handleSelectNewProjectTask = useCallback(
    (issueId, issue) => {
      if (
        formattedIssues[issueId]?.issueUniqueId ===
        formattedAnotherProjects[issueId]?.issueUniqueId
      ) {
        formattedIssues[`${issueId}_${counter.current}`] = {
          ...formattedAnotherProjects[issueId],
          issueUniqueId: `${issueId}_${counter.current}`,
          weekDays: formatWeek({ startOfWeek }),
          overTimeWeekDays: formatWeek({ startOfWeek }),
        };
        editedFormattedIssues[`${issueId}_${counter.current}`] =
          formattedAnotherProjects[issueId];
        counter.current = counter.current + 1;
      } else {
        formattedIssues[issueId] = {
          ...formattedAnotherProjects[issueId],
          weekDays: formattedIssues[issue.issueUniqueId].weekDays,
          overTimeWeekDays:
            formattedIssues[issue.issueUniqueId].overTimeWeekDays,
        };
        editedFormattedIssues[issueId] = formattedAnotherProjects[issueId];
      }
      delete formattedIssues[issue.issueUniqueId];
      forceUpdate();
    },
    [
      editedFormattedIssues,
      formattedAnotherProjects,
      formattedIssues,
      startOfWeek,
    ],
  );

  return (
    <>
      {isSmallScreen ? (
        <>
          <Grid
            container
            spacing={2}
            sx={{
              alignContent: 'center',
            }}
          >
            {Object.keys(formattedIssues).map(issueKey => {
              const projectIssue = formattedIssues[issueKey];
              return (
                projectIssue && (
                  <Grid
                    key={projectIssue.issueUniqueId}
                    sx={{ maxWidth: '50%', flexBasis: '50%' }}
                    size={{
                      xs: 6,
                      sm: 3,
                    }}
                  >
                    <SpreadsheetMobileIssueButton
                      projectIssue={projectIssue}
                      timeTypes={timeTypes}
                      timeType={timeType}
                      setQuery={setQuery}
                      query={query}
                    />
                  </Grid>
                )
              );
            })}
          </Grid>
        </>
      ) : (
        <StyledSpreadsheetTableRoot>
          <TableHead>
            <TableRow>
              <StyledTableCell align='left'>
                <Typography variant='h4' sx={{ fontWeight: 700 }}>
                  {getLocalString('SpreadsheetContainer', 'labels', 'project')}
                </Typography>
              </StyledTableCell>
              <StyledTableCell align='left'>
                <Typography variant='h4' sx={{ fontWeight: 700 }}>
                  {getLocalString('SpreadsheetContainer', 'labels', 'issue')}
                </Typography>
              </StyledTableCell>
              {weekDataHeader.map(day => (
                <StyledTableCellMedium align='center' key={day.date}>
                  <Typography variant='h5' sx={{ fontWeight: 700 }}>
                    {dayjs(day.date).format('DD.MM')}
                  </Typography>
                  <Typography variant='h5'>
                    {getLocalString(
                      'SpreadsheetContainer',
                      'labels',
                      day.dayOfWeek,
                    )}
                  </Typography>
                </StyledTableCellMedium>
              ))}
              <StyledTableCellSmall align='center'>
                <Typography variant='h4' sx={{ fontWeight: 700 }}>
                  {getLocalString('SpreadsheetContainer', 'labels', 'total')}
                </Typography>
              </StyledTableCellSmall>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(formattedIssues).map(issueKey => {
              const projectIssue = formattedIssues[issueKey];
              if (showOnlyFilledIn) {
                if (
                  timeType === timeTypes.MAIN_HOURS
                    ? projectIssue.totalNormalHours === 0
                    : projectIssue.totalOvertimeHours === 0
                ) {
                  return;
                }
              }
              if (
                timeType === timeTypes.MAIN_HOURS
                  ? projectIssue.hiddenMainHour
                  : projectIssue.hiddenOvertimeHour
              ) {
                return;
              }
              return (
                projectIssue && (
                  <SpreadsheetWeek
                    key={projectIssue.issueUniqueId}
                    projectIssue={projectIssue}
                    formattedAnotherProjects={formattedAnotherProjects}
                    anotherProjects={anotherProjects}
                    handleHideWeek={handleHideWeek}
                    handleChangeDayHour={handleChangeDayHour}
                    handleChangeDayComment={handleChangeDayComment}
                    handleSelectNewProject={handleSelectNewProject}
                    handleSelectNewProjectTask={handleSelectNewProjectTask}
                    timeTypes={timeTypes}
                    timeType={timeType}
                  />
                )
              );
            })}
            <StyledBottomRow>
              <StyledTableCell align='left'>
                <StyledAddRowButton
                  disableRipple
                  onClick={handleCreateNewProject}
                >
                  <PlusIcon width={24} height={24} viewBox='0 0 24 24' />
                </StyledAddRowButton>
              </StyledTableCell>
              <StyledTableCell align='right'>
                <Grid container direction='column' sx={{ paddingRight: 4 }}>
                  <Typography variant='h4' sx={{ fontWeight: 700 }}>
                    {getLocalString('SpreadsheetContainer', 'labels', 'total')}
                  </Typography>
                  {timeType === timeTypes.MAIN_HOURS && (
                    <Typography variant='h4' sx={{ fontWeight: 700 }}>
                      {getLocalString(
                        'SpreadsheetContainer',
                        'labels',
                        'overtime',
                      )}
                    </Typography>
                  )}
                </Grid>
              </StyledTableCell>
              {tableFooterDaysSum.map(daySum => {
                const isColored =
                  Number(daySum.totalNormalHours) !== daySum.expectedHours;
                return (
                  <StyledTableCellMedium align='right' key={daySum.date}>
                    <Grid container direction='column' sx={{ paddingRight: 3 }}>
                      {timeType === timeTypes.MAIN_HOURS ? (
                        <>
                          <Typography
                            variant='h4'
                            sx={{
                              color: isColored ? '#3448FF' : '#7174AC',
                            }}
                          >
                            {parseFloat(daySum.totalNormalHours.toFixed(1))}
                          </Typography>
                          <Typography
                            variant='h4'
                            sx={{
                              color: isColored ? '#3448FF' : '#7174AC',
                            }}
                          >
                            {parseFloat(
                              (
                                daySum.expectedHours - daySum.totalNormalHours
                              ).toFixed(1),
                            )}
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          variant='h4'
                          sx={{
                            color: '#7174AC',
                          }}
                        >
                          {daySum.totalOvertimeHours}
                        </Typography>
                      )}
                    </Grid>
                  </StyledTableCellMedium>
                );
              })}
              <StyledTableCellSmall align='center'>
                <Grid container direction='column'>
                  <Typography
                    variant='h4'
                    sx={{ color: workDayHours !== 0 ? '#3448FF' : 'inherit' }}
                  >
                    {parseFloat(Number(tableSum).toFixed(1))}
                  </Typography>
                  {timeType === timeTypes.MAIN_HOURS && (
                    <Typography
                      variant='h4'
                      sx={{ color: workDayHours !== 0 ? '#3448FF' : 'inherit' }}
                    >
                      {workDayHours}
                    </Typography>
                  )}
                </Grid>
              </StyledTableCellSmall>
            </StyledBottomRow>
          </TableBody>
        </StyledSpreadsheetTableRoot>
      )}
    </>
  );
};

export default memo(SpreadsheetTable);
